import { Materialized } from "@silversea-ssc/functional-support"
import { ActionTypes, ActorRefFrom, DoneInvokeEvent, EventObject, InterpreterFrom, StateMachine } from "xstate"
import { BadRequestCode } from "@ssc-booking/api-contracts"
import { RequestResponseChannel } from "./retry-data-load-machine"
import { RetryPolicy, RetryStatus } from "./retry-policy"

type InitialDataLoadContext<AReq> = {
    id: string
    retryStatus: RetryStatus
    lastCommand?: Load<AReq> | RetryLoad<AReq>
}

type LoadedResponse<ASuccess> = {
    response: ASuccess
}

type LoadedContext<AReq, ASuccess> = InitialDataLoadContext<AReq> & LoadedResponse<ASuccess>

type Error<T> = {
    type: "ERROR"
    error: T
}
type FatalError = {
    type: "FATAL"
    errorCode: FatalErrorCode
    message: string
    info: unknown
}

type ErrorResponse<AError> = {
    error: Error<AError> | FatalError
}

type ErrorContext<AReq, AError> = InitialDataLoadContext<AReq> & ErrorResponse<AError>

type DataLoadTypeState<AReq, ASuccess, AError> =
    | { value: "idle"; context: InitialDataLoadContext<AReq> }
    | { value: "loading"; context: InitialDataLoadContext<AReq> }
    | { value: "loaded"; context: LoadedContext<AReq, ASuccess> }
    | { value: "error"; context: ErrorContext<AReq, AError> }
    | { value: "retry"; context: InitialDataLoadContext<AReq> }

export type DataLoadContext<AReq, ASuccess, AError> = InitialDataLoadContext<AReq> &
    Partial<LoadedResponse<ASuccess>> &
    Partial<ErrorResponse<AError>>

type LoadSuccess<ASuccess> = { type: "LOAD_SUCCESS"; response: ASuccess }
export type LoadError<AError> = { type: "LOAD_ERROR"; response: AError }
export type FatalErrorCode = "connectionClosed" | "parsing" | "retriesExceeded" | BadRequestCode
export type LoadFatalError = { type: "LOAD_FATAL_ERROR"; errorCode: FatalErrorCode; message: string; info: unknown }
type Load<AReq> = { type: "LOAD"; data: AReq; country: string }
type RetryLoad<AReq> = { type: "RETRY_LOAD" } & Omit<Load<AReq>, "type">
export type DataLoadEvents<AReq, ASuccess, AError> =
    | Load<AReq>
    | LoadSuccess<ASuccess>
    | LoadError<AError>
    | LoadFatalError
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    | (DoneInvokeEvent<any> & { type: ActionTypes.DoneInvoke })
    | { type: "RETRY" }
    | RetryLoad<AReq>

export type DataLoadStateMachine<AReq, ASuccess, AError> = StateMachine<
    DataLoadContext<AReq, ASuccess, AError>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    DataLoadEvents<AReq, ASuccess, AError>,
    DataLoadTypeState<AReq, ASuccess, AError>
>

type DataLoadInterpreter<AReq, ASuccess, AError> = InterpreterFrom<DataLoadStateMachine<AReq, ASuccess, AError>>
type DataLoadState<AReq, ASuccess, AError> = DataLoadInterpreter<AReq, ASuccess, AError>["state"]
type DataLoadSend<AReq, ASuccess, AError> = DataLoadInterpreter<AReq, ASuccess, AError>["send"]
type ActorRef<AReq, ASuccess, AError> = ActorRefFrom<DataLoadStateMachine<AReq, ASuccess, AError>>
type DataLoadTypes<AReq, ASuccess, AError> = {
    Context: DataLoadContext<AReq, ASuccess, AError>
    LoadedContext: LoadedContext<AReq, ASuccess>
    ErrorContext: ErrorContext<AReq, AError>
    Events: DataLoadEvents<AReq, ASuccess, AError>
    LoadSuccess: LoadSuccess<ASuccess>
    LoadError: LoadError<AError>
    LoadFatalError: LoadFatalError
    Load: Load<AReq>
    TypeState: DataLoadTypeState<AReq, ASuccess, AError>
    StateMachine: DataLoadStateMachine<AReq, ASuccess, AError>
    Interpreter: DataLoadInterpreter<AReq, ASuccess, AError>
    State: DataLoadState<AReq, ASuccess, AError>
    Send: DataLoadSend<AReq, ASuccess, AError>
    ActorRef: ActorRef<AReq, ASuccess, AError>
    ShouldRetry: ShouldRetry<ASuccess, AError>
}

type ShouldRetry<ASuccess, AError> = (ev: LoadSuccess<ASuccess> | LoadError<AError> | LoadFatalError) => boolean

export type Config<EReq, AReq, ESuccess, ASuccess, EError, AError, TParentEvent extends EventObject> = {
    id?: string
    channel: RequestResponseChannel
    Request: Materialized<EReq, AReq>
    Success: Materialized<ESuccess, ASuccess>
    successType?: string
    Error: Materialized<EError, AError>
    errorType?: string
    action: string
    notifyParent: (
        ctx: DataLoadContext<AReq, ASuccess, AError>,
        ev: DataLoadEvents<AReq, ASuccess, AError>,
    ) => TParentEvent | undefined
    disableLog?: boolean
    shouldRetry?: ShouldRetry<ASuccess, AError>
    retryPolicy?: RetryPolicy
}

export const retryDataLoadTypes = <EReq, AReq, ESuccess, ASuccess, EError, AError>(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Request: Materialized<EReq, AReq>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Success: Materialized<ESuccess, ASuccess>,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    Error: Materialized<EError, AError>,
): DataLoadTypes<AReq, ASuccess, AError> => ({} as DataLoadTypes<AReq, ASuccess, AError>)

export const loadFatalError = (errorCode: FatalErrorCode, message: string, info: unknown): LoadFatalError => ({
    type: "LOAD_FATAL_ERROR",
    errorCode,
    message,
    info,
})
